import { useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Mail, UserPlus, Users, Trash2, Plus, Send, Check, X, ArrowRight, Loader2, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
    <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
  </svg>
);

export default function ContactImportModal({ onClose, onComplete }) {
  const [contacts, setContacts] = useState([{ name: '', email: '', phone: '' }]);
  const [bulkText, setBulkText] = useState('');
  const [mode, setMode] = useState('email'); // 'email', 'manual', 'bulk'
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [loadingMicrosoft, setLoadingMicrosoft] = useState(false);

  const addRow = () => setContacts([...contacts, { name: '', email: '', phone: '' }]);
  const removeRow = (i) => setContacts(contacts.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) => setContacts(contacts.map((c, idx) => idx === i ? { ...c, [field]: val } : c));

  const parseBulk = () => {
    const lines = bulkText.split('\n').filter(l => l.trim());
    const parsed = lines.map(line => {
      const parts = line.split(/[,\t;]+/).map(s => s.trim());
      const email = parts.find(p => p.includes('@')) || '';
      const phone = parts.find(p => /^\+?\d[\d\s\-()]{6,}$/.test(p)) || '';
      const name = parts.find(p => p !== email && p !== phone && p.length > 1) || '';
      return { name, email, phone };
    }).filter(c => c.email);
    if (parsed.length > 0) {
      setContacts(parsed);
      setMode('manual');
      toast.success(`Parsed ${parsed.length} contacts`);
    } else {
      toast.error('No valid contacts found. Make sure each line has an email.');
    }
  };

  const handleGoogleImport = async () => {
    setLoadingGoogle(true);
    try {
      const data = await api.getGoogleContactsAuthUrl();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error('Google import is not configured. Contact the admin.');
        setLoadingGoogle(false);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      toast.error(detail || 'Could not start Google import. Please try again.');
      setLoadingGoogle(false);
    }
  };

  const handleMicrosoftImport = async () => {
    setLoadingMicrosoft(true);
    try {
      const data = await api.getMicrosoftContactsAuthUrl();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (e) {
      toast.error('Could not start Microsoft import.');
      setLoadingMicrosoft(false);
    }
  };

  const handleImport = async () => {
    const valid = contacts.filter(c => c.email && c.email.includes('@'));
    if (valid.length === 0) { toast.error('Add at least one valid email'); return; }
    setImporting(true);
    try {
      const data = await api.importContacts(valid);
      setResults(data);
      toast.success(`Invited ${data.invited} contacts!`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  if (results) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-background rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-testid="import-results">
          <div className="p-6 border-b text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
              <Check className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-xl font-bold">Invites Sent!</h2>
            <p className="text-sm text-muted-foreground mt-1">Your contacts will receive an email invitation to join ConnectMailAgent</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-3 gap-3 text-center mb-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{results.invited}</p>
                <p className="text-[11px] text-muted-foreground">Invited</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{results.skipped_members}</p>
                <p className="text-[11px] text-muted-foreground">Already Members</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-600">{results.skipped_already_invited}</p>
                <p className="text-[11px] text-muted-foreground">Already Invited</p>
              </div>
            </div>
            {results.invited > 0 && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 text-center">
                <p className="text-sm font-semibold text-violet-900">When they join, you earn <strong>1 month free</strong> email signature per referral!</p>
              </div>
            )}
          </div>
          <div className="p-6 border-t">
            <Button className="w-full" onClick={() => { onComplete?.(); onClose(); }} data-testid="import-done-btn">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="contact-import-modal">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Grow Your Network</h2>
                <p className="text-xs text-muted-foreground">Import contacts from your email or add them manually</p>
              </div>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-import-modal">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Reward banner */}
          <div className="mt-4 bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-lg p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">$</div>
            <p className="text-xs text-violet-900"><strong>Earn 1 month free signature</strong> for each contact that joins! Auto-renewable, cancel anytime.</p>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="px-6 pt-4 flex gap-2">
          <Button variant={mode === 'email' ? 'default' : 'outline'} size="sm" onClick={() => setMode('email')} className="text-xs" data-testid="mode-email">
            <Inbox className="w-3.5 h-3.5 mr-1" />Import from Email
          </Button>
          <Button variant={mode === 'manual' ? 'default' : 'outline'} size="sm" onClick={() => setMode('manual')} className="text-xs" data-testid="mode-manual">
            <UserPlus className="w-3.5 h-3.5 mr-1" />Add Manually
          </Button>
          <Button variant={mode === 'bulk' ? 'default' : 'outline'} size="sm" onClick={() => setMode('bulk')} className="text-xs" data-testid="mode-bulk">
            <Mail className="w-3.5 h-3.5 mr-1" />Paste List
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {mode === 'email' ? (
            <div className="space-y-4">
              <div className="text-center space-y-2 pb-2">
                <p className="text-sm text-muted-foreground">
                  Connect your email to import <strong>everyone you've ever emailed</strong>. We only read email addresses — never your message content.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  variant="outline"
                  className="h-14 text-sm gap-3 border-2 hover:border-primary/40 hover:bg-primary/5"
                  onClick={handleGoogleImport}
                  disabled={loadingGoogle || loadingMicrosoft}
                  data-testid="modal-google-import-btn"
                >
                  {loadingGoogle ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon />}
                  Import from Gmail
                </Button>
                <Button
                  variant="outline"
                  className="h-14 text-sm gap-3 border-2 hover:border-primary/40 hover:bg-primary/5"
                  onClick={handleMicrosoftImport}
                  disabled={loadingGoogle || loadingMicrosoft}
                  data-testid="modal-microsoft-import-btn"
                >
                  {loadingMicrosoft ? <Loader2 className="w-4 h-4 animate-spin" /> : <MicrosoftIcon />}
                  Import from Outlook
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">
                Secure OAuth connection. We never store your email password.
              </p>
            </div>
          ) : mode === 'bulk' ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Paste contacts (one per line). Format: <code className="bg-muted px-1 py-0.5 rounded text-[10px]">Name, email@example.com, phone</code></p>
              <textarea
                value={bulkText}
                onChange={e => setBulkText(e.target.value)}
                placeholder={"John Smith, john@email.com, 555-123-4567\nJane Doe, jane@company.com\nrealtor@local.com"}
                className="w-full px-3 py-2.5 text-sm rounded-lg border border-input bg-background font-mono resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                rows={8}
                data-testid="bulk-input"
              />
              <Button size="sm" onClick={parseBulk} disabled={!bulkText.trim()} data-testid="parse-bulk">
                Parse Contacts
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {contacts.map((c, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`contact-row-${i}`}>
                  <Input value={c.name} onChange={e => updateRow(i, 'name', e.target.value)} placeholder="Name" className="text-xs h-9 flex-1" data-testid={`contact-name-${i}`} />
                  <Input value={c.email} onChange={e => updateRow(i, 'email', e.target.value)} placeholder="Email *" className="text-xs h-9 flex-[1.5]" type="email" data-testid={`contact-email-${i}`} />
                  <Input value={c.phone} onChange={e => updateRow(i, 'phone', e.target.value)} placeholder="Phone" className="text-xs h-9 w-28" data-testid={`contact-phone-${i}`} />
                  {contacts.length > 1 && (
                    <button onClick={() => removeRow(i)} className="text-muted-foreground hover:text-red-500 p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={addRow} className="text-xs w-full border border-dashed" data-testid="add-contact-row">
                <Plus className="w-3.5 h-3.5 mr-1" />Add Another Contact
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-xs">Skip for now</Button>
          {mode !== 'email' && (
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-[10px]">{contacts.filter(c => c.email?.includes('@')).length} valid contacts</Badge>
              <Button onClick={handleImport} disabled={importing || contacts.filter(c => c.email?.includes('@')).length === 0} data-testid="send-invites-btn">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {importing ? 'Sending...' : 'Send Invites'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
